import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import {
  addDays,
  addMinutes,
  format,
  isBefore,
  isToday,
  startOfTomorrow
} from 'date-fns'
import { disableBlueTapHighlight, thinScrollbar } from 'shop/components/common'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { formatTimeString } from 'shop/utils'
import FormInput from 'shop/components/Inputs/FormInput'
import { Dropdown } from 'shop/components/Inputs'
import {
  BsChevronRight as RightArrow,
  BsChevronUp as UpArrow
} from 'react-icons/bs'

type Props = {
  selectedFulfillmentDate: Date
  selectedFulfillmentTime: string | null
  setSelectedFulfillmentTime: Dispatch<SetStateAction<string | null>>
}

export function generateTimeSlots(
  fromDate = new Date(),
  intervalMinutes: number = 5
): string[] {
  let endOfDay = startOfTomorrow()
  const times: string[] = []

  let current = new Date()
  current.setSeconds(0)
  if (!isToday(fromDate)) {
    current = new Date(fromDate)
    current.setHours(0)
    current.setMinutes(0)
    current.setSeconds(0)
    endOfDay = addDays(current, 1) // midnight of the next future day
  }
  // Round up to the next interval
  const remainder = intervalMinutes - (current.getMinutes() % intervalMinutes)
  if (remainder !== intervalMinutes) {
    current = addMinutes(current, remainder)
  }

  while (isBefore(current, endOfDay)) {
    times.push(format(current, 'HH:mm:ss'))
    current = addMinutes(current, intervalMinutes)
  }

  return times
}

const TimeSelectDropdown = ({
  selectedFulfillmentDate,
  selectedFulfillmentTime,
  setSelectedFulfillmentTime
}: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const selectedTimeRef = useRef<HTMLLIElement>(null)
  const dropdownRef = useRef<HTMLUListElement>(null)

  const timeslots = generateTimeSlots(selectedFulfillmentDate, 15)
  const noTimeString = '--:--'

  // Scroll the selected Time into view, on load of component and any Date change
  const scrollToSelectedTime = () => {
    selectedTimeRef.current?.scrollIntoView({ behavior: 'auto' })
  }

  // if the date changes, try and match the time otherwise reset it
  useEffect(() => {
    if (!timeslots.includes(selectedFulfillmentTime || '')) {
      setSelectedFulfillmentTime(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFulfillmentDate])

  useEffect(() => {
    if (isDropdownOpen) scrollToSelectedTime()
  }, [isDropdownOpen])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <DropdownContainer>
      <FormInput
        value={formatTimeString(selectedFulfillmentTime, 'HH:mm')}
        labelText='Time'
        showError={false}
        readOnly={true}
        customInputEl={
          <Dropdown
            dropdownRef={dropdownRef}
            testId='select-time-dropdown'
            textContent={formatTimeString(selectedFulfillmentTime, 'HH:mm')}
            hasSelected={!!selectedFulfillmentTime}
            isOpen={isDropdownOpen}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            IconRight={isDropdownOpen ? <UpArrow /> : <RightArrow />}
            canEdit={true}
          >
            <>
              <TimeDropdownOption
                ref={!selectedFulfillmentTime ? selectedTimeRef : undefined}
                key={noTimeString}
                isSelected={!selectedFulfillmentTime}
                value={noTimeString}
                onClick={() => {
                  setSelectedFulfillmentTime(null)
                  setTimeout(() => setIsDropdownOpen(false), 100)
                }}
              >
                {noTimeString}
              </TimeDropdownOption>
              {timeslots.map((timeslot) => {
                const isSelected =
                  formatTimeString(timeslot, 'HH:mm') ===
                  formatTimeString(selectedFulfillmentTime, 'HH:mm')
                return (
                  <TimeDropdownOption
                    key={timeslot}
                    ref={isSelected ? selectedTimeRef : undefined}
                    isSelected={isSelected}
                    value={timeslot}
                    onClick={() => {
                      setSelectedFulfillmentTime(timeslot)
                      setTimeout(() => setIsDropdownOpen(false), 100)
                    }}
                  >
                    {formatTimeString(timeslot, 'HH:mm')}
                  </TimeDropdownOption>
                )
              })}
            </>
          </Dropdown>
        }
      />
    </DropdownContainer>
  )
}

const DropdownContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  width: '40%',
  '& button': {
    gap: '8px',
    paddingLeft: '15px',
    paddingRight: '15px',
    minWidth: '100px',

    '&:focus-within': {
      border: 'solid 1px #8B8B8B'
    }
  },
  '& ul': {
    top: 'calc(-350px - 12px)',
    maxHeight: '350px',
    border: 'solid 1px #8B8B8B',
    borderRadius: theme.borderRadius,
    backgroundColor: '#fff',
    ...thinScrollbar(theme)
  }
}))

const TimeDropdownOption = styled.li<
  StyledHTMLElement & { isSelected: boolean }
>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 0',

  color: isSelected ? '#fff' : '#000',
  backgroundColor: isSelected ? theme.colors['primary'] : '#fff',

  fontSize: '16px',
  fontWeight: 500,

  cursor: 'pointer',
  ...disableBlueTapHighlight(),

  '&:hover': {
    backgroundColor: isSelected ? theme.colors['primary'] : '#F0F0F0',
    opacity: 0.8
  },
  '&:active': {
    backgroundColor: isSelected ? theme.colors['primary'] : '#F0F0F0',
    opacity: 0.6
  }
}))

export default TimeSelectDropdown
